import { CornerDownLeft, X } from "lucide-react";

import { cn } from "../../lib/cn";
import { InputGroup, InputGroupInput } from "../../primitive/input-group";

import { Badge } from "../badge";

import { type TagValidationResult, useTagInput } from "./use-tag-input";

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  validateTag?: (tag: string, currentTags: string[]) => TagValidationResult;
  transformOnAdd?: (tag: string) => string | null;
  onValidationError?: (result: TagValidationResult, tag: string) => void;
  inputFilter?: (value: string) => boolean;
  disabled?: boolean;
  className?: string;
  showEnterHintBadge?: boolean;
  enterHintText?: string;
}

export function TagInput({
  value,
  onChange,
  placeholder = "Enter tag",
  validateTag,
  transformOnAdd,
  onValidationError,
  inputFilter,
  disabled = false,
  className,
  showEnterHintBadge = false,
  enterHintText = "Enter to add",
}: TagInputProps) {
  const { inputValue, handleInputChange, handleKeyDown, removeTag } =
    useTagInput({
      value,
      onChange,
      validateTag,
      transformOnAdd,
      onValidationError,
      inputFilter,
    });

  return (
    <InputGroup
      className={cn(
        "min-h-9 h-auto flex-wrap gap-1 p-2",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
      data-disabled={disabled}
    >
      {value.map((tag, index) => (
        <Badge
          key={`${tag}-${index}`}
          variant="secondary"
          className="flex items-center gap-1"
        >
          <span>{tag}</span>
          {!disabled ? (
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="m-0 flex shrink-0 items-center rounded-sm transition-colors hover:bg-gray-200"
              aria-label={`Remove ${tag}`}
            >
              <X className="size-3" />
            </button>
          ) : null}
        </Badge>
      ))}
      <InputGroupInput
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? `${placeholder} ↵` : "↵"}
        disabled={disabled}
        className="h-6 min-w-20 flex-1 px-1 text-sm"
      />
      {showEnterHintBadge && !disabled && inputValue.trim().length > 0 ? (
        <Badge variant="secondary" className="pointer-events-none gap-1">
          <CornerDownLeft className="size-3" />
          {enterHintText}
        </Badge>
      ) : null}
    </InputGroup>
  );
}
